import PlanManagerModal from "../../../../common/PlanManagerModal";

/**
 * Copy for the confirmation modals rendered by the page.
 * @readonly
 */
export const MODAL_COPY = Object.freeze({
    EXCLUDE_PRE_ENROLLMENT: {
        title: "Are you sure you want to exclude the pre-enrollment site?",
        body: "There will be no PE site for this client, as none of the networks are selected to be included.",
        primary: "Save",
        secondary: "Cancel",
    },
    SAVE_WITHOUT_PUBLISH: {
        title: "Save your work without publishing the pre-enrollment site",
        body: "The configuration is completed but you have opted not to publish. You can come back at a later date to publish.",
        primary: "Not yet, just save my work",
        secondary: "Publish pre-enrollment site",
    },
    PLAN_AFFILIATION_WARNING: {
        title: "One or more plans are not associated with a network",
        body: "Provider and network data will not be available for these plans.",
        primary: "Keep working",
        secondary: "Cancel",
    },
    UNSAVED_WORK: {
        title: "Unsaved work!",
        body: "Your work has not been saved and will be lost.",
        primary: "Leave this page",
        secondary: "Continue working",
    },
    DELETE_NETWORK: {
        title: "Delete network?",
        body: "This network is already associated to a plan. All plan affiliations are going to be wiped. Deleting this will result in \u201Cno network or provider information\u201D across all platforms.",
        primary: "Delete",
        secondary: "Keep working",
    },
    CONFIRM_EDIT_URL: {
        title: "You are attempting to change the site URL",
        body: "Please ensure you have informed the member comms team prior to making this change as it will have impacts to member materials.",
        primary: "I acknowledge",
    },
    UNPUBLISH: {
        title: "Are you sure you want to unpublish?",
        body: "You only need to unpublish the site in order to make changes to the URL or content. To terminate the site, please submit an ITS ticket.",
        primary: "I acknowledge",
        secondary: "No, cancel",
    },
    PUBLISH: {
        title: "Confirm publishing",
        body: "If you publish the pre-enrollment site it will be accessible to the public.",
        primary: "Yes, publish",
        secondary: "No, cancel",
    },
    REMOVE_CUSTOM_CONTENT: {
        title: "Are you sure you want to proceed?",
        body: "All customizations will be permanently lost. This action cannot be undone.",
        primary: "Yes",
        secondary: "Cancel",
    },
});

/**
 * Renders every confirmation modal for the Networks-config page from a flag map
 * and a handler map. Presentational — all behavior flows through `handlers`;
 * owns no state.
 *
 * @param {object} props
 * @param {Object} props.flags - Open/closed booleans keyed by modal name.
 * @param {Object} props.handlers - Confirm/close callbacks keyed by modal name.
 * @returns {import('react').JSX.Element}
 */
const PageModals = ({ flags, handlers }) => (
    <>
        <PlanManagerModal
            isOpen={flags.excludePreEnrollment}
            title={MODAL_COPY.EXCLUDE_PRE_ENROLLMENT.title}
            body={MODAL_COPY.EXCLUDE_PRE_ENROLLMENT.body}
            primaryText={MODAL_COPY.EXCLUDE_PRE_ENROLLMENT.primary}
            secondaryText={MODAL_COPY.EXCLUDE_PRE_ENROLLMENT.secondary}
            onPrimary={handlers.onExcludePreEnrollmentSave}
            onSecondary={handlers.onCloseExcludePreEnrollment}
            onClose={handlers.onCloseExcludePreEnrollment}
        />
        <PlanManagerModal
            isOpen={flags.saveWithoutPublish}
            title={MODAL_COPY.SAVE_WITHOUT_PUBLISH.title}
            body={MODAL_COPY.SAVE_WITHOUT_PUBLISH.body}
            primaryText={MODAL_COPY.SAVE_WITHOUT_PUBLISH.primary}
            secondaryText={MODAL_COPY.SAVE_WITHOUT_PUBLISH.secondary}
            onPrimary={handlers.onSaveWithoutPublishSave}
            onSecondary={handlers.onSaveWithoutPublishPublish}
            onClose={handlers.onCloseSaveWithoutPublish}
        />
        <PlanManagerModal
            isOpen={flags.planAffiliationWarning}
            title={MODAL_COPY.PLAN_AFFILIATION_WARNING.title}
            body={MODAL_COPY.PLAN_AFFILIATION_WARNING.body}
            primaryText={MODAL_COPY.PLAN_AFFILIATION_WARNING.primary}
            secondaryText={MODAL_COPY.PLAN_AFFILIATION_WARNING.secondary}
            onPrimary={handlers.onClosePlanAffiliationWarning}
            onSecondary={handlers.onClosePlanAffiliationWarning}
            onClose={handlers.onClosePlanAffiliationWarning}
        />
        <PlanManagerModal
            isOpen={flags.unsavedWork}
            title={MODAL_COPY.UNSAVED_WORK.title}
            body={MODAL_COPY.UNSAVED_WORK.body}
            primaryText={MODAL_COPY.UNSAVED_WORK.primary}
            secondaryText={MODAL_COPY.UNSAVED_WORK.secondary}
            onPrimary={handlers.onUnsavedWorkLeave}
            onSecondary={handlers.onUnsavedWorkContinue}
            onClose={handlers.onUnsavedWorkContinue}
            tone="accent"
        />
        <PlanManagerModal
            isOpen={flags.deleteNetwork}
            title={MODAL_COPY.DELETE_NETWORK.title}
            body={MODAL_COPY.DELETE_NETWORK.body}
            primaryText={MODAL_COPY.DELETE_NETWORK.primary}
            secondaryText={MODAL_COPY.DELETE_NETWORK.secondary}
            onPrimary={handlers.onDeleteNetworkConfirm}
            onSecondary={handlers.onCloseDeleteNetwork}
            onClose={handlers.onCloseDeleteNetwork}
        />
        <PlanManagerModal
            isOpen={flags.confirmEditUrl}
            title={MODAL_COPY.CONFIRM_EDIT_URL.title}
            body={MODAL_COPY.CONFIRM_EDIT_URL.body}
            primaryText={MODAL_COPY.CONFIRM_EDIT_URL.primary}
            onPrimary={handlers.onConfirmEditUrl}
            onClose={handlers.onCloseConfirmEditUrl}
        />
        <PlanManagerModal
            isOpen={flags.publish}
            title={MODAL_COPY.PUBLISH.title}
            body={MODAL_COPY.PUBLISH.body}
            primaryText={MODAL_COPY.PUBLISH.primary}
            secondaryText={MODAL_COPY.PUBLISH.secondary}
            onPrimary={handlers.onConfirmPublish}
            onSecondary={handlers.onClosePublish}
            onClose={handlers.onClosePublish}
        />
        <PlanManagerModal
            isOpen={flags.unpublish}
            title={MODAL_COPY.UNPUBLISH.title}
            body={MODAL_COPY.UNPUBLISH.body}
            primaryText={MODAL_COPY.UNPUBLISH.primary}
            secondaryText={MODAL_COPY.UNPUBLISH.secondary}
            onPrimary={handlers.onConfirmUnpublish}
            onSecondary={handlers.onCloseUnpublish}
            onClose={handlers.onCloseUnpublish}
        />
        <PlanManagerModal
            isOpen={flags.removeCustomContent}
            title={MODAL_COPY.REMOVE_CUSTOM_CONTENT.title}
            body={MODAL_COPY.REMOVE_CUSTOM_CONTENT.body}
            primaryText={MODAL_COPY.REMOVE_CUSTOM_CONTENT.primary}
            secondaryText={MODAL_COPY.REMOVE_CUSTOM_CONTENT.secondary}
            onPrimary={handlers.onConfirmRemoveCustomContent}
            onSecondary={handlers.onCloseRemoveCustomContent}
            onClose={handlers.onCloseRemoveCustomContent}
        />
    </>
);

export default PageModals;
